import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { loadQualificationTypeDetails } from '../../../store/directusService';
import {
    loadParticipantPastEducation,
    updateParticipantPastEducation
} from '../../../store/participantService';
import { NO_CURRENT_PARTICIPANT_ERROR } from '../../../utils/formValidation/commonErrorConstants';
import { QUALIFICATION_TYPE_ERROR } from '../../../utils/formValidation/loadingErrorMessageConstants';
import { sortByEndDate } from '../../../utils/sortFunctions';
import {
    ADVISER,
    hasRole,
    MANAGER,
    QUALITY,
    RECRUITMENT_MANAGER,
    SUPERUSER
} from '../../../utils/userRoles';
import Button from '../../formElements/Button';
import IconError from '../../IconError';
import LoadingSpinner from '../../ui/LoadingSpinner';

import EditEducationCard from './EditEducationCard';
import EducationCard from './EducationCard';
import { validate } from './validateEducation';

import app from '../../../app.module.css';
import cards from '../../../commonStyles/cardContainer.module.css';
import local from './education.module.css';

const _MESSAGE = 'Participant past education details have been updated';
const acceptedRoles = [ADVISER, MANAGER, QUALITY, RECRUITMENT_MANAGER, SUPERUSER];
const editOnClosedRoles = [SUPERUSER, QUALITY];

const Education = () => {
    const dispatch = useDispatch();
    // LOCAL STATE
    const [institutions, setInstitutions] = useState([]);
    const [currentInstitution, setCurrentInstitution] = useState({});
    const [editType, setEditType] = useState('create');
    const [isOpenEditor, setIsOpenEditor] = useState(false);
    const [isDisabled, setIsDisabled] = useState(true);
    const [isUpdateDisabled, setIsUpdateDisabled] = useState(false);
    const [errors, setErrors] = useState({});

    // STORE STATE
    const roles = useSelector((state) => state.entities.userService.loggedInUser.roles);
    const qualificationTypeDetails = useSelector(
        (state) => state.entities.directusService.qualificationTypeDetails
    );
    const currentParticipant = useSelector(
        (state) => state.entities.participantService.currentParticipant
    );
    const pastEducation = useSelector(
        (state) => state.entities.participantService.currentParticipantPastEducation
    );
    const successMessage = useSelector((state) => state.entities.formsState.successMessage);

    // USE EFFECTS
    useEffect(() => {
        if (!('status' in currentParticipant)) return;
        const disableOnClosedStatus =
            currentParticipant.status === 'Closed' && !hasRole(editOnClosedRoles, roles);
        setIsDisabled(!hasRole(acceptedRoles, roles) || disableOnClosedStatus);
    }, [currentParticipant.status, roles]);

    useEffect(() => {
        if (currentParticipant?.id) {
            dispatch(loadParticipantPastEducation(currentParticipant.id));
            dispatch(loadQualificationTypeDetails());
        }
    }, [currentParticipant.id]);

    useEffect(() => {
        if (!('institutions' in pastEducation)) return;
        const sortedEducation = sortByEndDate([...pastEducation.institutions]);
        setInstitutions(sortedEducation || []);
    }, [pastEducation]);

    useEffect(() => {
        setIsUpdateDisabled(Object.values(errors).some((el) => el.error));
    }, [errors]);

    useEffect(() => {
        if (successMessage === _MESSAGE) setIsUpdateDisabled(false);
    }, [successMessage]);

    // HELPER FNS
    const loadingError = () => {
        if (qualificationTypeDetails?.length < 1) return QUALIFICATION_TYPE_ERROR;
        if (Object.keys(currentParticipant)?.length < 1) return NO_CURRENT_PARTICIPANT_ERROR;
    };

    const clearError = (key) => {
        if (key in errors && errors[key].error)
            setErrors((prev) => ({ ...prev, [key]: { error: false, message: '' } }));
    };

    // EVENT HANDLERS
    const onAddCard = () => {
        setEditType('create');
        setIsOpenEditor(true);
    };

    const addInstitution = (newInstitution) => {
        clearError('institutions');
        setIsOpenEditor(false);
        setInstitutions((prev) => sortByEndDate([...prev, newInstitution]));
    };

    const updateInstitution = (institution) => {
        setIsOpenEditor(false);
        setCurrentInstitution({});
        setInstitutions(institutions.map((el) => (el.id === institution.id ? institution : el)));
    };

    const deleteInstitution = (id) => {
        setInstitutions(institutions.filter((el) => el.id !== id));
    };

    const onEditInstitution = (institution) => {
        setCurrentInstitution(institution);
        setEditType('edit');
        setIsOpenEditor(true);
    };

    const onSubmit = (e) => {
        e.preventDefault();
        setIsUpdateDisabled(true);
        const newErrors = validate(institutions, pastEducation.institutions);
        if (Object.keys(newErrors).length) {
            setErrors(newErrors);
            return;
        }
        const payload = {
            institutions: institutions.map((el) => ({ ...el, id: null }))
        };
        dispatch(updateParticipantPastEducation(payload, currentParticipant.id, _MESSAGE));
    };

    // AWAITING CONTENT
    const errorMsg = loadingError();
    if (errorMsg) return <LoadingSpinner content={errorMsg} />;

    // RENDER
    return (
        <div className={local.educationWrapper}>
            <h2 className={app.sectionHeading}>Past Education</h2>
            <div className={cards.cardContainer}>
                <div className={cards.cards}>
                    {institutions?.map((el) => (
                        <div key={el.id}>
                            <EducationCard
                                institution={el}
                                qualificationTypes={qualificationTypeDetails}
                                isDisabled={isDisabled}
                                onEditInstitution={onEditInstitution}
                                onDeleteInstitution={deleteInstitution}
                            />
                        </div>
                    ))}
                    {isOpenEditor && (
                        <EditEducationCard
                            institution={currentInstitution}
                            currentParticipant={currentParticipant}
                            qualificationTypes={qualificationTypeDetails}
                            isDisabled={isDisabled}
                            editType={editType}
                            isOpen={isOpenEditor}
                            onCancel={() => setIsOpenEditor(false)}
                            onUpdate={updateInstitution}
                            onAdd={addInstitution}
                        />
                    )}
                </div>
                <div className={local.actions}>
                    <Button
                        id="addPastEducationCard"
                        type="button"
                        onClick={onAddCard}
                        disabled={isDisabled}
                        content="Add Past Education"
                    />

                    {errors.institutions?.error && (
                        <div className={local.screenError}>
                            <IconError text={errors.institutions} />
                        </div>
                    )}

                    <div>
                        <Button
                            id="updateEducationButton"
                            testId="updateEducationButton"
                            disabled={isDisabled || isUpdateDisabled}
                            content="Update Past Education"
                            customErrorClass="nonFormError"
                            error={errors.button}
                            clearError={() => clearError('button')}
                            onClick={onSubmit}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Education;
